import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Wind, Clock, ArrowLeft, User, TrendingUp, StickyNote, Pencil, Check, X, UserCheck } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/utils/constants';
import { useAuth } from '@/contexts/AuthContext';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

// Custom tooltip component for charts
const CustomTooltip = ({ active, payload, color, unit = 's' }) => {
  if (active && payload && payload.length) {
    const v = payload[0].value || 0;
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200">
        <p className="text-xs text-slate-500">{payload[0].payload.fullDate}</p>
        <p className="text-lg font-bold" style={{ color }}>
          {unit === 'pas' ? `${v} pas` : `${v.toFixed(1)}s`}
        </p>
      </div>
    );
  }
  return null;
};

const AdminUserHistoryPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { startImpersonation } = useAuth();
  
  const [isMounted, setIsMounted] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState({ total_sessions: 0, total_minutes: 0, total_cycles: 0 });
  const [challengeScores, setChallengeScores] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsWindow, setAnalyticsWindow] = useState(30);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('records');
  const [isEditingName, setIsEditingName] = useState(false);
  const [editNameValue, setEditNameValue] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [openingSession, setOpeningSession] = useState(false);
  
  const [records, setRecords] = useState({
    inhale: { time: 0, date: null },
    exhale: { time: 0, date: null },
    hold: { time: 0, date: null },
    apnea: { time: 0, date: null },
    mbt: { steps: 0, date: null }
  });

  // Mark component as mounted for client-side only rendering (fixes recharts)
  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    // Redirect non-admin users
    if (currentUser && !currentUser.is_admin) {
      navigate('/app');
      toast.error("Accès réservé aux administrateurs");
      return;
    }
    
    const loadData = async () => {
      try {
        const [statsRes, sessionsRes, challengesRes, analyticsRes] = await Promise.all([
          axios.get(`${API}/admin/users/${userId}/stats`),
          axios.get(`${API}/admin/users/${userId}/sessions`),
          axios.get(`${API}/admin/users/${userId}/challenges`),
          axios.get(`${API}/admin/users/${userId}/analytics?days=${analyticsWindow}`).catch(() => ({ data: null })),
        ]);
        
        setUserInfo(statsRes.data.user || null);
        setStats(statsRes.data.stats || { total_sessions: 0, total_minutes: 0, total_cycles: 0 });
        setSessions(Array.isArray(sessionsRes.data) ? sessionsRes.data : []);
        setChallengeScores(Array.isArray(challengesRes.data) ? challengesRes.data : []);
        setAnalytics(analyticsRes.data || null);
      } catch (err) {
        if (err.response?.status === 403) {
          toast.error("Accès réservé aux administrateurs");
          navigate('/app');
        } else if (err.response?.status === 404) {
          toast.error("Utilisateur non trouvé");
          navigate('/admin');
        } else {
          toast.error("Erreur lors du chargement des données");
        }
      } finally {
        setLoading(false);
      }
    };
    
    if (currentUser?.is_admin && userId) {
      loadData();
    }
  }, [userId, currentUser, navigate, analyticsWindow]);

  // Calculate records from challenge scores
  useEffect(() => {
    if (challengeScores && challengeScores.length > 0) {
      const safeFilter = (type) => challengeScores.filter(s => s && s.challenge_type === type && typeof s.duration_seconds === 'number');
      const mbtFilter = () => challengeScores.filter(s => s && s.challenge_type === 'mbt' && typeof s.steps === 'number');
      
      const inhaleRecords = safeFilter('inhale');
      const exhaleRecords = safeFilter('exhale');
      const holdRecords = safeFilter('hold');
      const apneaRecords = safeFilter('apnea');
      const mbtRecords = mbtFilter();

      const bestInhale = inhaleRecords.length > 0 
        ? inhaleRecords.reduce((max, s) => (s.duration_seconds || 0) > (max.duration_seconds || 0) ? s : max)
        : null;
      const bestExhale = exhaleRecords.length > 0 
        ? exhaleRecords.reduce((max, s) => (s.duration_seconds || 0) > (max.duration_seconds || 0) ? s : max)
        : null;
      const bestHold = holdRecords.length > 0 
        ? holdRecords.reduce((max, s) => (s.duration_seconds || 0) > (max.duration_seconds || 0) ? s : max)
        : null;
      const bestApnea = apneaRecords.length > 0 
        ? apneaRecords.reduce((max, s) => (s.duration_seconds || 0) > (max.duration_seconds || 0) ? s : max)
        : null;
      const bestMbt = mbtRecords.length > 0 
        ? mbtRecords.reduce((max, s) => (s.steps || 0) > (max.steps || 0) ? s : max)
        : null;

      setRecords({
        inhale: { 
          time: bestInhale ? bestInhale.duration_seconds : 0,
          date: bestInhale ? bestInhale.completed_at : null
        },
        exhale: { 
          time: bestExhale ? bestExhale.duration_seconds : 0,
          date: bestExhale ? bestExhale.completed_at : null
        },
        hold: { 
          time: bestHold ? bestHold.duration_seconds : 0,
          date: bestHold ? bestHold.completed_at : null
        },
        apnea: { 
          time: bestApnea ? bestApnea.duration_seconds : 0,
          date: bestApnea ? bestApnea.completed_at : null
        },
        mbt: { 
          steps: bestMbt ? (bestMbt.steps || 0) : 0,
          date: bestMbt ? bestMbt.completed_at : null
        }
      });
    }
  }, [challengeScores]);

  // Prepare chart data for each challenge type
  const chartData = useMemo(() => {
    if (!challengeScores || !Array.isArray(challengeScores)) {
      return { inhale: [], exhale: [], hold: [], apnea: [], mbt: [] };
    }
    
    const prepareData = (type) => {
      const filtered = challengeScores
        .filter(s => s && s.challenge_type === type && s.completed_at)
        .sort((a, b) => new Date(a.completed_at) - new Date(b.completed_at));
      
      return filtered.map((score, index) => {
        const date = new Date(score.completed_at);
        return {
          xKey: index + 1,
          score: type === 'mbt' ? (score.steps || 0) : (score.duration_seconds || 0),
          date: date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }),
          fullDate: date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })
        };
      });
    };

    return {
      inhale: prepareData('inhale'),
      exhale: prepareData('exhale'),
      hold: prepareData('hold'),
      apnea: prepareData('apnea'),
      mbt: prepareData('mbt')
    };
  }, [challengeScores]);

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const formatRecordDate = (dateStr) => {
    if (!dateStr) return 'Pas encore de record';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const getChallengeLabel = (type) => {
    switch (type) {
      case 'inhale': return 'Inspiration';
      case 'exhale': return 'Expiration';
      case 'hold': return 'Apnée (BOLT)';
      case 'apnea': return 'Apnée';
      case 'mbt': return 'MBT';
      default: return type;
    }
  };

  const getChallengeColor = (type) => {
    switch (type) {
      case 'inhale': return { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-600', hex: '#d97706' };
      case 'exhale': return { bg: 'bg-sky-50', border: 'border-sky-200', text: 'text-sky-600', hex: '#0284c7' };
      case 'hold': return { bg: 'bg-violet-50', border: 'border-violet-200', text: 'text-violet-600', hex: '#7c3aed' };
      case 'apnea': return { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-600', hex: '#059669' };
      case 'mbt': return { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-600', hex: '#e11d48' };
      default: return { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-600', hex: '#6b7280' };
    }
  };

  // Render progression chart for a challenge type
  const renderChart = (type, title, gradientFrom, gradientTo) => {
    const data = chartData[type];
    const isMbt = type === 'mbt';
    const record = isMbt ? records.mbt.steps : records[type]?.time || 0;
    const unit = isMbt ? 'pas' : 's';
    const recordDisplay = isMbt ? `${record} pas` : `${(record || 0).toFixed(1)}s`;
    const colors = getChallengeColor(type);

    if (!isMounted) {
      return (
        <Card className={`${colors.bg} ${colors.border} mb-4`} key={type}>
          <CardContent className="py-4">
            <h3 className={`font-semibold ${colors.text} mb-2`}>{title}</h3>
            <div className="h-48 flex items-center justify-center">
              <p className="text-sm text-slate-500">Chargement...</p>
            </div>
          </CardContent>
        </Card>
      );
    }

    if (!data || data.length === 0) {
      return (
        <Card className={`${colors.bg} ${colors.border} mb-4`} key={type}>
          <CardContent className="py-4">
            <h3 className={`font-semibold ${colors.text} mb-2`}>{title}</h3>
            <p className="text-sm text-slate-500 text-center py-4">Aucune donnée disponible</p>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card className={`bg-gradient-to-br ${gradientFrom} ${gradientTo} border-0 shadow-lg mb-4`} key={type}>
        <CardContent className="py-4">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-semibold text-slate-800">{title}</h3>
            <div className="text-right">
              <span className="text-xs text-slate-500">Record: </span>
              <span className={`font-bold ${colors.text}`}>{recordDisplay}</span>
            </div>
          </div>
          <div className="h-48 w-full" style={{ minHeight: '192px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis 
                  dataKey="xKey" 
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  tickLine={false}
                  axisLine={{ stroke: '#e2e8f0' }}
                  tickFormatter={(value) => {
                    const point = data.find(d => d.xKey === value);
                    return point ? point.date : '';
                  }}
                />
                <YAxis 
                  tick={{ fontSize: 10, fill: '#64748b' }}
                  tickLine={false}
                  axisLine={{ stroke: '#e2e8f0' }}
                  unit={unit === 'pas' ? '' : 's'}
                  domain={[0, 'auto']}
                />
                <Tooltip content={<CustomTooltip color={colors.hex} unit={unit} />} />
                {record > 0 && (
                  <ReferenceLine 
                    y={record} 
                    stroke={colors.hex} 
                    strokeDasharray="5 5" 
                    strokeWidth={2}
                    label={{ value: 'Record', position: 'right', fontSize: 10, fill: colors.hex }}
                  />
                )}
                <Line 
                  type="monotone" 
                  dataKey="score" 
                  stroke={colors.hex}
                  strokeWidth={3}
                  dot={{ fill: colors.hex, strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, fill: colors.hex }}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className="text-xs text-slate-500 text-center mt-2">
            {data.length} entrée{data.length > 1 ? 's' : ''} enregistrée{data.length > 1 ? 's' : ''}
          </p>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-indigo-50 flex items-center justify-center">
        <div className="text-stone-400">Chargement...</div>
      </div>
    );
  }

  const handleStartEditName = () => {
    setEditNameValue(userInfo?.name || '');
    setIsEditingName(true);
  };

  const handleCancelEditName = () => {
    setIsEditingName(false);
    setEditNameValue('');
  };

  const handleSaveName = async () => {
    const trimmed = (editNameValue || '').trim();
    if (!trimmed) {
      toast.error("Le nom ne peut pas être vide");
      return;
    }
    if (trimmed === userInfo?.name) {
      setIsEditingName(false);
      return;
    }
    try {
      setSavingName(true);
      await axios.put(`${API}/admin/users/${userId}/name`, { name: trimmed });
      setUserInfo((prev) => (prev ? { ...prev, name: trimmed } : prev));
      toast.success("Nom du patient mis à jour");
      setIsEditingName(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de la mise à jour du nom");
    } finally {
      setSavingName(false);
    }
  };

  const handleOpenPatientSession = async () => {
    if (openingSession) return;
    const confirmMsg = `Ouvrir la séance de ${userInfo?.name || 'ce patient'} ?\n\n` +
      `Vous allez agir comme ce patient pendant 2 h maximum. Tous les exercices, défis et check-ins seront enregistrés dans son historique. ` +
      `Vous pourrez revenir à votre compte admin à tout moment via le bouton "Terminer la séance".`;
    if (!window.confirm(confirmMsg)) return;
    try {
      setOpeningSession(true);
      await startImpersonation(userId);
      toast.success(`Séance ouverte avec ${userInfo?.name || 'le patient'}`);
      navigate('/mon-espace');
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Impossible d'ouvrir la séance du patient");
    } finally {
      setOpeningSession(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-indigo-50 pb-24">
      <div className="max-w-md mx-auto px-6 pt-8">
        {/* Back button and user info */}
        <button
          onClick={() => navigate('/admin')}
          className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800 mb-4 transition-colors"
        >
          <ArrowLeft size={18} />
          <span className="text-sm font-medium">Retour à la liste</span>
        </button>

        {userInfo && (
          <Card className="bg-gradient-to-r from-indigo-500 to-indigo-600 border-0 mb-4">
            <CardContent className="py-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <User className="text-white" size={24} />
                </div>
                <div className="text-white flex-1 min-w-0">
                  {isEditingName ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={editNameValue}
                        onChange={(e) => setEditNameValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveName();
                          if (e.key === 'Escape') handleCancelEditName();
                        }}
                        autoFocus
                        disabled={savingName}
                        placeholder="Prénom Nom"
                        className="flex-1 px-2 py-1 rounded-md text-slate-800 text-base font-semibold outline-none focus:ring-2 focus:ring-white"
                        data-testid="admin-user-name-input"
                      />
                      <button
                        type="button"
                        onClick={handleSaveName}
                        disabled={savingName}
                        className="p-1.5 bg-white/20 hover:bg-white/30 rounded-md transition disabled:opacity-50"
                        title="Enregistrer"
                        data-testid="admin-user-name-save"
                      >
                        <Check size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={handleCancelEditName}
                        disabled={savingName}
                        className="p-1.5 bg-white/20 hover:bg-white/30 rounded-md transition disabled:opacity-50"
                        title="Annuler"
                        data-testid="admin-user-name-cancel"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <h1 className="text-xl font-bold truncate">{userInfo.name}</h1>
                      <button
                        type="button"
                        onClick={handleStartEditName}
                        className="p-1 rounded-md hover:bg-white/20 transition flex-shrink-0"
                        title="Modifier le nom (format recommandé : Prénom Nom)"
                        data-testid="admin-user-name-edit"
                      >
                        <Pencil size={14} />
                      </button>
                    </div>
                  )}
                  <p className="text-indigo-100 text-sm truncate">{userInfo.email}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Admin accompaniment session — "Ouvrir sa séance" */}
        {userInfo && (
          <button
            type="button"
            onClick={handleOpenPatientSession}
            disabled={openingSession}
            className="w-full mb-6 flex items-center justify-center gap-2 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-semibold py-3 rounded-xl shadow-md transition disabled:opacity-60"
            data-testid="admin-open-patient-session"
          >
            <UserCheck size={18} />
            {openingSession ? 'Ouverture de la séance…' : 'Ouvrir sa séance'}
          </button>
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <Card className="bg-white/70 backdrop-blur border-stone-200">
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-teal-600">{stats.total_sessions}</p>
              <p className="text-xs text-stone-500">Sessions</p>
            </CardContent>
          </Card>
          <Card className="bg-white/70 backdrop-blur border-stone-200">
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-teal-600">{stats.total_minutes}</p>
              <p className="text-xs text-stone-500">Minutes</p>
            </CardContent>
          </Card>
          <Card className="bg-white/70 backdrop-blur border-stone-200">
            <CardContent className="pt-4 text-center">
              <p className="text-2xl font-bold text-teal-600">{stats.total_cycles}</p>
              <p className="text-xs text-stone-500">Cycles</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full bg-white/70 backdrop-blur mb-4">
            <TabsTrigger value="records" className="flex-1 text-xs sm:text-sm">Records</TabsTrigger>
            <TabsTrigger value="progression" className="flex-1 text-xs sm:text-sm">Progression</TabsTrigger>
            <TabsTrigger value="challenges" className="flex-1 text-xs sm:text-sm">Défis</TabsTrigger>
            <TabsTrigger value="sessions" className="flex-1 text-xs sm:text-sm">Sessions</TabsTrigger>
            <TabsTrigger value="analytics" className="flex-1 text-xs sm:text-sm">Activité</TabsTrigger>
          </TabsList>

          <TabsContent value="records">
            <div className="space-y-3">
              <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
                <CardContent className="py-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium text-amber-800">Inspiration Max</p>
                      <p className="text-xs text-amber-600 mt-1">{formatRecordDate(records.inhale.date)}</p>
                    </div>
                    <p className="text-2xl font-bold text-amber-600">
                      {records.inhale.time.toFixed(1)}s
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-r from-sky-50 to-indigo-50 border-sky-200">
                <CardContent className="py-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium text-sky-800">Expiration Max</p>
                      <p className="text-xs text-sky-600 mt-1">{formatRecordDate(records.exhale.date)}</p>
                    </div>
                    <p className="text-2xl font-bold text-sky-600">
                      {records.exhale.time.toFixed(1)}s
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-r from-violet-50 to-purple-50 border-violet-200">
                <CardContent className="py-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium text-violet-800">Apnée Max (BOLT)</p>
                      <p className="text-xs text-violet-600 mt-1">{formatRecordDate(records.hold.date)}</p>
                    </div>
                    <p className="text-2xl font-bold text-violet-600">
                      {records.hold.time.toFixed(1)}s
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-200">
                <CardContent className="py-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium text-emerald-800">Apnée</p>
                      <p className="text-xs text-emerald-600 mt-1">{formatRecordDate(records.apnea.date)}</p>
                    </div>
                    <p className="text-2xl font-bold text-emerald-600">
                      {records.apnea.time.toFixed(1)}s
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-r from-rose-50 to-pink-50 border-rose-200">
                <CardContent className="py-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="font-medium text-rose-800">MBT (pas)</p>
                      <p className="text-xs text-rose-600 mt-1">{formatRecordDate(records.mbt.date)}</p>
                    </div>
                    <p className="text-2xl font-bold text-rose-600">
                      {records.mbt.steps} pas
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="progression">
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="text-indigo-600" size={20} />
                <h2 className="font-semibold text-slate-800">Graphiques de Progression</h2>
              </div>
              
              {renderChart('inhale', 'Progression Inspiration', 'from-amber-50', 'to-orange-50')}
              {renderChart('exhale', 'Progression Expiration', 'from-sky-50', 'to-indigo-50')}
              {renderChart('hold', 'Progression Apnée (BOLT)', 'from-violet-50', 'to-purple-50')}
              {renderChart('apnea', 'Progression Apnée', 'from-emerald-50', 'to-green-50')}
              {renderChart('mbt', 'Progression MBT', 'from-rose-50', 'to-pink-50')}
            </div>
          </TabsContent>

          <TabsContent value="challenges">
            {challengeScores.length === 0 ? (
              <Card className="bg-white/70 backdrop-blur border-stone-200">
                <CardContent className="py-8 text-center">
                  <Clock className="mx-auto mb-3 text-stone-300" size={40} />
                  <p className="text-stone-500">Aucun défi enregistré</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {challengeScores.filter(s => s && (typeof s.duration_seconds === 'number' || s.challenge_type === 'mbt')).map((score, idx) => {
                  const colors = getChallengeColor(score.challenge_type);
                  return (
                    <Card key={score.id || idx} className={`${colors.bg} ${colors.border}`}>
                      <CardContent className="py-3">
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className={`font-medium ${colors.text}`}>
                                {getChallengeLabel(score.challenge_type)}
                              </p>
                              {score.is_record && (
                                <span className="text-[10px] bg-yellow-400 text-yellow-900 px-1.5 py-0.5 rounded-full font-bold">
                                  RECORD
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-stone-500 mt-1">{formatDate(score.completed_at)}</p>
                          </div>
                          <p className={`text-xl font-bold ${colors.text}`}>
                            {score.challenge_type === 'mbt'
                              ? `${score.steps || 0} pas`
                              : `${(score.duration_seconds || 0).toFixed(1)}s`}
                          </p>
                        </div>
                        {score.notes && (
                          <p className="mt-2 text-xs text-stone-600 bg-white/70 border border-stone-200 rounded-lg px-3 py-2 whitespace-pre-wrap" data-testid={`admin-challenge-note-display-${score.id}`}>
                            <StickyNote size={12} className="inline mr-1 text-amber-500" />
                            {score.notes}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="sessions">
            {sessions.length === 0 ? (
              <Card className="bg-white/70 backdrop-blur border-stone-200">
                <CardContent className="py-8 text-center">
                  <Wind className="mx-auto mb-3 text-stone-300" size={40} />
                  <p className="text-stone-500">Aucune session</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {sessions.filter(s => s && s.pattern_name).map((session, idx) => (
                  <Card key={session.id || idx} className="bg-white/70 backdrop-blur border-stone-200">
                    <CardContent className="py-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-stone-700">{session.pattern_name}</p>
                          <p className="text-xs text-stone-400 mt-1">{formatDate(session.completed_at)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-teal-600">{(session.duration_minutes || 0).toFixed(1)} min</p>
                          <p className="text-xs text-stone-400">{session.cycles_completed ?? 0} cycles</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="analytics">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <span className="text-xs text-slate-500 font-medium">Période :</span>
              {[7, 30, 90, 365].map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setAnalyticsWindow(d)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold border transition ${analyticsWindow === d ? 'bg-sky-600 text-white border-sky-600 shadow' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'}`}
                  data-testid={`analytics-window-${d}`}
                >
                  {d} j
                </button>
              ))}
            </div>

            {!analytics ? (
              <p className="text-sm text-slate-500 text-center py-8">Aucune donnée d&apos;activité disponible.</p>
            ) : (
              <div className="space-y-4">
                {/* KPI cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-gradient-to-br from-sky-50 to-cyan-50 border border-sky-200 rounded-xl p-3">
                    <p className="text-[10px] uppercase tracking-wide text-sky-700 font-bold">Ouvertures / connexions</p>
                    <p className="text-2xl font-bold text-sky-900 mt-1">{analytics.logins_window}</p>
                    <p className="text-[10px] text-slate-500 mt-0.5">total : {analytics.logins_all_time}</p>
                  </div>
                  <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-3">
                    <p className="text-[10px] uppercase tracking-wide text-emerald-700 font-bold">Pages vues</p>
                    <p className="text-2xl font-bold text-emerald-900 mt-1">{analytics.page_views_window}</p>
                    <p className="text-[10px] text-slate-500 mt-0.5">total : {analytics.page_views_all_time}</p>
                  </div>
                  <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-3">
                    <p className="text-[10px] uppercase tracking-wide text-amber-700 font-bold">Jours actifs</p>
                    <p className="text-2xl font-bold text-amber-900 mt-1">{analytics.active_days_window}</p>
                    <p className="text-[10px] text-slate-500 mt-0.5">sur {analytics.window_days} j</p>
                  </div>
                  <div className="bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200 rounded-xl p-3">
                    <p className="text-[10px] uppercase tracking-wide text-violet-700 font-bold">Dernière connexion</p>
                    <p className="text-sm font-semibold text-violet-900 mt-1 leading-tight">
                      {analytics.last_login ? new Date(analytics.last_login).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                    </p>
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      {analytics.last_login ? new Date(analytics.last_login).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : ''}
                    </p>
                  </div>
                </div>

                {/* Daily activity chart */}
                {analytics.activity_by_day && analytics.activity_by_day.length > 0 && (
                  <Card className="bg-white border-slate-200">
                    <CardContent className="py-4">
                      <h3 className="font-semibold text-slate-800 mb-3 text-sm">Activité par jour</h3>
                      <div className="h-56 w-full">
                        {isMounted && (
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={analytics.activity_by_day} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                              <XAxis
                                dataKey="date"
                                tick={{ fontSize: 10, fill: '#64748b' }}
                                tickLine={false}
                                axisLine={{ stroke: '#e2e8f0' }}
                                tickFormatter={(v) => {
                                  const d = new Date(v);
                                  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
                                }}
                              />
                              <YAxis
                                tick={{ fontSize: 10, fill: '#64748b' }}
                                tickLine={false}
                                axisLine={{ stroke: '#e2e8f0' }}
                                allowDecimals={false}
                              />
                              <Tooltip
                                labelFormatter={(v) => new Date(v).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                                contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '12px' }}
                              />
                              <Line type="monotone" dataKey="page_views" name="Pages" stroke="#10B981" strokeWidth={2} dot={{ r: 3 }} />
                              <Line type="monotone" dataKey="logins" name="Connexions" stroke="#0EA5E9" strokeWidth={2} dot={{ r: 3 }} />
                            </LineChart>
                          </ResponsiveContainer>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Top paths */}
                <Card className="bg-white border-slate-200">
                  <CardContent className="py-4">
                    <h3 className="font-semibold text-slate-800 mb-3 text-sm">Pages les plus consultées</h3>
                    {analytics.top_paths && analytics.top_paths.length > 0 ? (
                      <div className="space-y-1.5">
                        {analytics.top_paths.map((row) => {
                          const max = analytics.top_paths[0].count || 1;
                          const pct = Math.max(4, Math.round((row.count / max) * 100));
                          return (
                            <div key={row.path} className="flex items-center gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-baseline justify-between gap-2">
                                  <p className="text-xs text-slate-700 font-mono truncate" title={row.path}>{row.path}</p>
                                  <span className="text-xs font-bold text-slate-600 flex-shrink-0">{row.count}</span>
                                </div>
                                <div className="mt-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                                  <div className="h-full bg-gradient-to-r from-sky-500 to-cyan-500" style={{ width: `${pct}%` }} />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-500 text-center py-3">Aucune navigation enregistrée sur la période.</p>
                    )}
                  </CardContent>
                </Card>

                {/* Recent events */}
                <Card className="bg-white border-slate-200">
                  <CardContent className="py-4">
                    <h3 className="font-semibold text-slate-800 mb-3 text-sm">Derniers événements</h3>
                    {analytics.recent_events && analytics.recent_events.length > 0 ? (
                      <div className="max-h-80 overflow-y-auto -mx-4 px-4">
                        <ul className="divide-y divide-slate-100">
                          {analytics.recent_events.map((ev, i) => (
                            <li key={i} className="py-2 flex items-start gap-2">
                              <span className={`mt-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide ${ev.event_type === 'login' ? 'bg-sky-100 text-sky-800' : 'bg-emerald-100 text-emerald-800'}`}>
                                {ev.event_type === 'login' ? 'Login' : 'Page'}
                              </span>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-slate-700 font-mono truncate">{ev.path || '—'}</p>
                                <p className="text-[10px] text-slate-500">
                                  {new Date(ev.timestamp).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </p>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <p className="text-xs text-slate-500 text-center py-3">Aucun événement récent.</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminUserHistoryPage;
